/**
 * Utility functions for TasksPage
 *
 * @module pages/tasks/utils
 */

import type { TaskStatus } from '@/hooks/graphql/useTaskMutationsGraphQL';

// ============================================================================
// Date Formatting
// ============================================================================

/**
 * Format a Date object as YYYY-MM-DD string in local timezone
 */
export function formatYMD(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/**
 * Format a date for display as a section header
 *
 * @param dateStr - Date string in YYYY-MM-DD format (dateKey from task.createdAt)
 */
export function formatDateHeader(dateStr: string): string {
  // Get today and yesterday as YYYY-MM-DD strings in local timezone
  const now = new Date();
  const todayStr = formatYMD(now);

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = formatYMD(yesterday);

  // Compare date strings directly to avoid timezone issues
  if (dateStr === todayStr) {
    return 'Today';
  }
  if (dateStr === yesterdayStr) {
    return 'Yesterday';
  }

  // For older dates, parse and format
  // Add time component to avoid UTC interpretation issues
  const date = new Date(dateStr + 'T12:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

// ============================================================================
// Label Helpers
// ============================================================================

/**
 * Get action type label for display
 */
export function getActionTypeLabel(actionType: string): string {
  const labels: Record<string, string> = {
    VIEW_PHOTO: 'View Photo',
    COMMENT_ON_PHOTO: 'Comment on Photo',
    RATE_PHOTO: 'Rate Photo',
    REPLY_TO_COMMENT: 'Reply to Comment',
    MANUAL: 'Custom Task',
  };
  return labels[actionType] || actionType;
}

/**
 * Get status badge color classes
 */
export function getStatusColor(status: TaskStatus): string {
  switch (status) {
    case 'pending':
      return 'bg-amber-500/20 text-amber-700 dark:text-amber-400';
    case 'completed':
      return 'bg-success/20 text-success';
    case 'dismissed':
      return 'bg-muted/20 text-muted';
    default:
      return 'bg-primary/20 text-primary';
  }
}
